// src/oura/screens/ClientDocuments.tsx
//
// Documents → Client documents (staff): every document made or shared for this
// client, in one list (Jovi, 21 Oct).
//
//   Reports            the Baseline Snapshot, progress reports, AI checks and
//                      staff uploads: the client page's `reports`, the list the
//                      Client tab's Reports card and the merchant's Reports read.
//   Contract           the engagement's own documents (the quotation, the
//                      execution timeline): the same list, `kind: "document"`.
//   Coded documents    every doc in the client's Drive folder, once each, in one
//                      list ordered by the contract's codes; a doc's chips say
//                      which codes it covers. A Code dropdown narrows it to one
//                      code ("S1: Existing pages labelled"), and a doc covering
//                      several codes shows under each of them (Jovi, 21 Oct).
//                      Same-named copies (four "Answer Plan" S1 S3 B1 G4) fold
//                      under one row, the one shared with the client first, and
//                      every row says its version and when it was last edited.
//
// A coded doc opens in the same drawer as on the Client tab (DocSheet), right
// here, with previous and next through the list on screen; a report opens in
// the PDF viewer, the Baseline Snapshot drawn in-app (ReportDrawer) (Jovi, 21 Oct).
//
// Loading shows placeholder rows, never a 0. Each source fails on its own: a
// failed reports read keeps the folder's docs on screen, and the reverse.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLinkIcon, FolderOpenIcon, SearchIcon } from "lucide-react";
import { listBusinessReports, type ClientReportLink } from "@service/aeo/clientReview";
import type { CodeRow, StaffDocument } from "@service/aeo/clientDocs";
import { agoText, codeFamily, codeLabel, docDate, docsFailureText, staffDocTitle, useBusinessDocuments } from "@oura/docs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { Drawer, FamilyChip, PillButton, PillLink, Seg, TYPE, drawerNav, drawerSequence, type SegItem } from "@oura/ui";
import { cn } from "../lib/cn";
import { ouraHref } from "../nav";
import { Card, CardBody, CardHeader } from "../primitives/Card";
import { EmptyState } from "../primitives/EmptyState";
import { DocRow, LoadingRows } from "../components/docs/DocRow";
import { docSheetTitle, groupBoard } from "../components/docs/boardModel";
import { DocSheet } from "../components/docs/DocDrawer";
import type { DocWriteView } from "../components/docs/write/model";
import { lastLineFor, nextLive, type SheetFlow } from "../components/client/drawerFlow";
import { ReportDrawer } from "../merchant/drawers/ReportDrawer";
import { ReportRow } from "../components/client/ReportsCard";
import { byNewest } from "../components/client/reportRows";

type Show = "all" | "reports" | "contract" | "coded";

type ReportsRead = { kind: "loading" } | { kind: "failed" } | { kind: "ready"; reports: ClientReportLink[] };

const COPY = {
    title: "Client documents",
    description: "Everything Oura made or shared for this client: reports, the contract's documents, and every doc in the client's folder by code.",
    openFolder: "Open Drive folder",
    search: "Search by name or code",
    reports: "Reports",
    contract: "Contract",
    coded: "Coded documents",
    reportsSub: "PDFs Oura made or staff uploaded for this client.",
    contractSub: "What was sold, and the dated plan that came from it.",
    codedSub: "Every doc in the client's Drive folder, under the codes it covers.",
    allCodes: "All codes",
    others: (n: number) => `Show ${n} other ${n === 1 ? "version" : "versions"}`,
    hideOthers: "Hide other versions",
    edited: (ago: string) => `Edited ${ago}`,
    notInContract: "Not in the contract",
    codeFilter: "Code",
    noCode: "No code yet",
    internal: "Marked internal",
    reportsFailed: "Couldn't read this client's reports.",
    noFolder: "No Drive folder is linked for this client yet.",
    linkFolder: "Link it on the Client tab",
    retry: "Try again",
    noMatch: (q: string) => `Nothing matches “${q}”.`,
    none: "None yet",
    emptyTitle: "No documents yet",
    emptyBody: "Reports, the contract's documents and the client's coded docs show here as they are made.",
} as const;

/** The client page's reports, read once per business, and again when the tab comes back to the front. */
function useReports(businessId: string): { read: ReportsRead; retry: () => void } {
    const [read, setRead] = useState<ReportsRead>({ kind: "loading" });
    const seq = useRef(0);
    const load = useCallback(() => {
        const mine = ++seq.current;
        listBusinessReports(businessId)
            .then(({ reports, failed }) => {
                if (mine === seq.current) setRead(failed ? { kind: "failed" } : { kind: "ready", reports: [...reports].sort(byNewest) });
            })
            .catch(() => {
                if (mine === seq.current) setRead({ kind: "failed" });
            });
    }, [businessId]);
    useEffect(() => {
        setRead({ kind: "loading" });
        load();
        const onVisible = () => {
            if (document.visibilityState === "visible") load();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            seq.current += 1;
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [load]);
    return { read, retry: load };
}

/** One choice in the Code dropdown: a code (with its outlet) and the docs that cover it. */
interface CodeOption {
    /** "S2"; "__none__" and "__internal__" for the two non-code choices. */
    key: string;
    /** The chip: "S2", every outlet's doc included. Null for the non-code choices. */
    chip: string | null;
    code: string | null;
    /** "S1: Existing pages labelled", as the dropdown and the line under it read. */
    text: string;
    ids: Set<string>;
}

/** Docs with the same title and codes: one row, the others folded under it. */
interface DocFamily {
    key: string;
    lead: StaffDocument;
    others: StaffDocument[];
}

interface CodedList {
    /** Every doc once: by its first code in the contract's order, then no code, then internal. */
    docs: StaffDocument[];
    /** The same docs, same-named copies folded: the list's rows. */
    families: DocFamily[];
    /** The codes with at least one doc, in the contract's order, then No code yet and Marked internal. */
    options: CodeOption[];
}

/**
 * No hairline between rows: only the sections are split by a rule (Jovi, 21 Oct).
 * Report rows draw a top border, doc rows a ::before line; both are switched off
 * here only, so the Client tab keeps its lines. Literal, so the JIT sees it.
 */
const NO_ROW_LINES = "[&>*]:border-t-0 [&_[data-doc-row]]:before:hidden";

const NONE = "__none__";
const INTERNAL = "__internal__";

function codedList(codes: CodeRow[], documents: StaffDocument[]): CodedList {
    const rank = new Map<string, number>();
    // One choice per code, outlets folded in: S2 covers S2-BB and S2-MK (Jovi, 21 Oct).
    const byCode = new Map<string, CodeOption>();
    const options: CodeOption[] = [];
    codes.forEach((row, i) => {
        const ids = row.documentIds.filter((id) => documents.some((d) => d.id === id && !d.notClientDoc));
        ids.forEach((id) => rank.set(id, Math.min(rank.get(id) ?? i, i)));
        if (!ids.length) return;
        let option = byCode.get(row.code);
        if (!option) {
            const name = row.label ?? (row.inContract ? null : COPY.notInContract);
            option = { key: row.code, chip: row.code, code: row.code, text: name ? `${row.code}: ${name}` : row.code, ids: new Set() };
            byCode.set(row.code, option);
            options.push(option);
        }
        ids.forEach((id) => option?.ids.add(id));
    });
    const client = documents.filter((d) => !d.notClientDoc);
    const noCode = client.filter((d) => !rank.has(d.id));
    const internal = documents.filter((d) => d.notClientDoc);
    if (noCode.length) options.push({ key: NONE, chip: null, code: null, text: COPY.noCode, ids: new Set(noCode.map((d) => d.id)) });
    if (internal.length) options.push({ key: INTERNAL, chip: null, code: null, text: COPY.internal, ids: new Set(internal.map((d) => d.id)) });
    const coded = client.filter((d) => rank.has(d.id)).sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
    const docs = [...coded, ...noCode, ...internal];
    return { docs, families: familiesOf(docs), options };
}

/** "1.10" after "1.9": a version read as numbers, part by part. */
function versionParts(v: string | null): number[] {
    return (v ?? "").split(".").map((n) => Number.parseInt(n, 10)).filter((n) => !Number.isNaN(n));
}

/** Which copy leads its family: the one the client can see, then the highest version, then the latest edit. */
function leadsOver(a: StaffDocument, b: StaffDocument): boolean {
    if (a.sharing.clientCanSee !== b.sharing.clientCanSee) return a.sharing.clientCanSee;
    const x = versionParts(a.version);
    const y = versionParts(b.version);
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
        if ((x[i] ?? -1) !== (y[i] ?? -1)) return (x[i] ?? -1) > (y[i] ?? -1);
    }
    return Date.parse(a.modifiedAt ?? "") > Date.parse(b.modifiedAt ?? "");
}

/** Folds same-named copies, keeping the list's order (each family sits where its first copy did). */
function familiesOf(docs: StaffDocument[]): DocFamily[] {
    const out = new Map<string, StaffDocument[]>();
    for (const d of docs) {
        const name = d.codes.length ? staffDocTitle(d) : d.name;
        const key = `${d.notClientDoc ? "i" : "c"}|${name.trim().toLowerCase()}|${[...d.codes].sort().join(",")}|${d.outlet ?? ""}`;
        out.set(key, [...(out.get(key) ?? []), d]);
    }
    return Array.from(out, ([key, copies]) => {
        const lead = copies.reduce((best, d) => (leadsOver(d, best) ? d : best));
        return { key, lead, others: copies.filter((d) => d !== lead) };
    });
}

/** The quiet line under a doc's title: "v2.0 · 19 Oct 2026 · Edited yesterday". */
function versionLine(d: StaffDocument): string | null {
    const ago = agoText(d.modifiedAt);
    const parts = [d.version ? `v${d.version}` : null, docDate(d.docDate), ago ? COPY.edited(ago) : null].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
}

const has = (text: string | null | undefined, q: string) => !!text && text.toLowerCase().includes(q);

export function ClientDocuments({ businessId, businessName }: { businessId: string; businessName: string }) {
    const navigate = useNavigate();
    const board = useBusinessDocuments(businessId);
    const { read: reportsRead, retry: retryReports } = useReports(businessId);
    const [show, setShow] = useState<Show>("all");
    const [query, setQuery] = useState("");
    const q = query.trim().toLowerCase();

    const reports = reportsRead.kind === "ready" ? reportsRead.reports : null;
    const made = useMemo(() => reports?.filter((r) => r.kind !== "document") ?? null, [reports]);
    const contract = useMemo(() => reports?.filter((r) => r.kind === "document") ?? null, [reports]);
    const coded = useMemo(() => (board.data ? codedList(board.data.codes ?? [], board.data.documents ?? []) : null), [board.data]);
    const [codeKey, setCodeKey] = useState<string>("all");
    const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
    const toggleFamily = (key: string) =>
        setUnfolded((was) => {
            const next = new Set(was);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    // The Client tab's own reading of the board: when an unshared doc was due.
    const boardModel = useMemo(() => (board.data ? groupBoard({ documents: board.data.documents ?? [], codes: board.data.codes ?? [] }) : null), [board.data]);
    const pickedCode = coded?.options.find((o) => o.key === codeKey) ?? null;
    const codedCount = board.data ? (board.data.documents ?? []).length : null;

    // The search reads what a row shows: a report's title, a doc's title and file name, and its codes.
    const matchReport = (r: ClientReportLink) => !q || has(r.title, q);
    const matchDoc = (d: StaffDocument) =>
        !q ||
        has(staffDocTitle(d), q) ||
        has(d.name, q) ||
        d.codes.some((c) => has(codeLabel(c, d.outlet), q)) ||
        !!coded?.options.some((o) => o.ids.has(d.id) && has(o.text, q));
    // One filtered list for the rows AND the drawer's previous/next (Jack, #4500): a family shows only
    // its copies in the picked code that match the search, the first of them leading.
    const visibleFamilies: DocFamily[] = coded
        ? coded.families.flatMap((family) => {
              const visible = [family.lead, ...family.others].filter((doc) => (!pickedCode || pickedCode.ids.has(doc.id)) && matchDoc(doc));
              return visible.length ? [{ key: family.key, lead: visible[0], others: visible.slice(1) }] : [];
          })
        : [];

    // The doc drawer, as the Client tab's board runs it: one shell, a sheet keyed per doc.
    const [openDocId, setOpenDocId] = useState<string | null>(null);
    const [sheetView, setSheetView] = useState<{ id: string; mode: "doc" | "pick" | DocWriteView; entry: "foot" | "row" } | null>(null);
    const [busy, setBusy] = useState(false);
    const [openReport, setOpenReport] = useState<ClientReportLink | null>(null);
    const openDoc = (id: string) => {
        setSheetView(null);
        setBusy(false);
        setOpenDocId(id);
    };
    const pickCode = (id: string) => {
        setSheetView({ id, mode: "pick", entry: "row" });
        setBusy(false);
        setOpenDocId(id);
    };
    const closeSheet = () => {
        setSheetView(null);
        setBusy(false);
        setOpenDocId(null);
    };
    const folderUrl = board.data?.folder?.url ?? null;

    const items: SegItem<Show>[] = [
        { key: "all", label: "All", count: made && contract && codedCount !== null ? made.length + contract.length + codedCount : undefined },
        { key: "reports", label: COPY.reports, count: made?.length },
        { key: "contract", label: COPY.contract, count: contract?.length },
        { key: "coded", label: "Coded", count: codedCount ?? undefined },
    ];

    const reportSection = (title: string, sub: string, rows: ClientReportLink[] | null) => {
        if (reportsRead.kind === "failed") return <Section title={title} sub={sub} count={null}><Failed text={COPY.reportsFailed} onRetry={retryReports} /></Section>;
        if (!rows) return <Section title={title} sub={sub} count={null}><LoadingRows count={2} /></Section>;
        const shown = rows.filter(matchReport);
        // An empty group still says so, so a "0" in the filter has a line to point at. A search hides it.
        if (!shown.length) return q ? null : <Section title={title} sub={sub} count={0}><None /></Section>;
        return (
            <Section title={title} sub={sub} count={shown.length}>
                <div className={NO_ROW_LINES}>{shown.map((r) => <ReportRow key={r.id} report={r} onOpen={setOpenReport} />)}</div>
            </Section>
        );
    };

    const codedSection = () => {
        if (board.status === "loading") return <Section title={COPY.coded} sub={COPY.codedSub} count={null}><LoadingRows count={3} /></Section>;
        if (!board.data) {
            return (
                <Section title={COPY.coded} sub={COPY.codedSub} count={null}>
                    <Failed text={board.error ? docsFailureText(board.error, "the client's folder") : COPY.noFolder} onRetry={board.reload} />
                </Section>
            );
        }
        if (!board.data.folder) {
            return (
                <Section title={COPY.coded} sub={COPY.codedSub} count={null}>
                    <p className={cn(TYPE.tc, "m-0 py-3 text-gray-500")}>
                        {COPY.noFolder}{" "}
                        <button type="button" className="font-medium text-blue-700 hover:underline" onClick={() => navigate(ouraHref("reviews", { role: "oura-staff", businessId }))}>
                            {COPY.linkFolder}
                        </button>
                    </p>
                </Section>
            );
        }
        if (!coded) return null;
        const row = (d: StaffDocument) => (
            <DocRow key={d.id} doc={d} onOpen={openDoc} onPick={pickCode} showStatus due={boardModel?.dueByDoc.get(d.id) ?? null} meta={versionLine(d)} viewLabel="View" />
        );
        const picker = coded.options.length > 0 && (
            <Select value={pickedCode ? codeKey : "all"} onValueChange={setCodeKey}>
                <SelectTrigger aria-label={COPY.codeFilter} className="h-9 w-full text-sm sm:w-[320px]">
                    <SelectValue />
                </SelectTrigger>
                {/* Opens from the trigger's right edge: the trigger sits at the section's right (Jovi, 21 Oct). */}
                <SelectContent align="end">
                    <SelectItem value="all">
                        {COPY.allCodes} <span className="text-gray-400">({coded.docs.length})</span>
                    </SelectItem>
                    {coded.options.map((o) => (
                        <SelectItem key={o.key} value={o.key}>
                            {o.text} <span className="text-gray-400">({o.ids.size})</span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
        return (
            <Section title={COPY.coded} sub={COPY.codedSub} count={coded.docs.length} aside={picker}>
                {pickedCode?.chip && pickedCode.code && (
                    // The picked code, in full: the dropdown's line can truncate.
                    <p className={cn(TYPE.tc, "m-0 mb-2 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-gray-700")}>
                        <FamilyChip family={codeFamily(pickedCode.code)}>{pickedCode.chip}</FamilyChip>
                        <span className="min-w-0">{pickedCode.text.slice(pickedCode.chip.length + 2) || pickedCode.chip}</span>
                    </p>
                )}
                {visibleFamilies.length ? (
                    <div className={NO_ROW_LINES}>
                        {visibleFamilies.map((f) => {
                            const open = unfolded.has(f.key);
                            return (
                                <div key={f.key}>
                                    {row(f.lead)}
                                    {f.others.length > 0 && (
                                        <div className="pl-11">
                                            <button
                                                type="button"
                                                aria-expanded={open}
                                                onClick={() => toggleFamily(f.key)}
                                                className={cn(TYPE.tc, "mb-1 font-medium text-blue-700 hover:underline")}
                                            >
                                                {open ? COPY.hideOthers : COPY.others(f.others.length)}
                                            </button>
                                            {open && <div className="border-l border-gray-100 pl-3">{f.others.map(row)}</div>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : q ? (
                    <p className={cn(TYPE.tc, "m-0 py-2 text-gray-400")}>{COPY.noMatch(query.trim())}</p>
                ) : (
                    <None />
                )}
            </Section>
        );
    };

    // Previous and next walk the coded docs on screen, in the list's order (every copy, folded or not).
    const docById = new Map((board.data?.documents ?? []).map((d) => [d.id, d]));
    const seqIds = visibleFamilies.flatMap((f) => [f.lead, ...f.others].map((d) => d.id));
    const titleOf = (id: string) => {
        const d = docById.get(id);
        return d ? docSheetTitle(d) : null;
    };
    const openBoardDoc = openDocId ? docById.get(openDocId) ?? null : null;
    const mode = openDocId && sheetView?.id === openDocId ? sheetView.mode : "doc";
    const entry = openDocId && sheetView?.id === openDocId ? sheetView.entry : "foot";
    const seq = openDocId ? drawerSequence(seqIds, openDocId, titleOf) : null;
    const nav = seq && mode === "doc" ? drawerNav(seq, { group: COPY.coded, onMove: openDoc, keys: true, disabled: busy }) : undefined;
    const next = openDocId ? nextLive(seqIds, openDocId, titleOf) : null;
    const flow: SheetFlow = {
        inGroup: Boolean(openBoardDoc),
        group: COPY.coded,
        left: seq?.left ?? 0,
        next,
        onNext: () => {
            if (next) openDoc(next.id);
        },
        onDone: closeSheet,
        lastLine: lastLineFor(COPY.coded),
        onBusy: setBusy,
        onOpenDoc: openDoc,
    };

    const sections: ReactNode[] = [
        (show === "all" || show === "reports") && <Fragment key="r">{reportSection(COPY.reports, COPY.reportsSub, made)}</Fragment>,
        (show === "all" || show === "contract") && <Fragment key="c">{reportSection(COPY.contract, COPY.contractSub, contract)}</Fragment>,
        (show === "all" || show === "coded") && <Fragment key="d">{codedSection()}</Fragment>,
    ];
    const allRead = reportsRead.kind === "ready" && board.status !== "loading";
    const nothingAtAll = allRead && made?.length === 0 && contract?.length === 0 && (codedCount ?? 0) === 0 && !!board.data?.folder;
    const nothingShown =
        allRead && !nothingAtAll && sectionsEmpty(show, made, contract, coded, q, matchReport, (d) => matchDoc(d) && (!pickedCode || pickedCode.ids.has(d.id)));

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader
                    icon={FolderOpenIcon}
                    title={COPY.title}
                    description={COPY.description}
                    aside={
                        folderUrl ? (
                            <PillLink href={folderUrl} target="_blank" rel="noreferrer" size="sm" icon={ExternalLinkIcon}>
                                {COPY.openFolder}
                            </PillLink>
                        ) : undefined
                    }
                />
                <CardBody className="space-y-4 border-t border-gray-100 pt-4">
                    {nothingAtAll ? (
                        <EmptyState icon={FolderOpenIcon} title={COPY.emptyTitle} description={COPY.emptyBody} />
                    ) : (
                        <>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <Seg ariaLabel="Show" items={items} value={show} onChange={setShow} size="sm" />
                                <label className="relative block w-full sm:w-64">
                                    <span className="sr-only">{COPY.search}</span>
                                    <SearchIcon aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder={COPY.search}
                                        className="h-9 w-full rounded-xl border-0 bg-gray-50 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    />
                                </label>
                            </div>
                            {nothingShown ? (
                                <p className={cn(TYPE.tb, "m-0 py-6 text-center text-gray-500")}>{COPY.noMatch(query.trim())}</p>
                            ) : (
                                // Each section is a heading of its own, split by a rule (Jovi, 21 Oct).
                                <div className="divide-y divide-gray-100">{sections}</div>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>

            <Drawer
                open={Boolean(openDocId)}
                onClose={closeSheet}
                nav={nav}
                back={
                    openDocId && mode !== "doc" && entry === "foot"
                        ? { label: openBoardDoc ? docSheetTitle(openBoardDoc) : "Document", onBack: () => setSheetView({ id: openDocId, mode: "doc", entry: "foot" }) }
                        : undefined
                }
                itemKey={openDocId ? `${openDocId}:${mode}` : undefined}
            >
                {openDocId && (
                    <DocSheet
                        key={openDocId}
                        docId={openDocId}
                        boardDoc={openBoardDoc}
                        board={board.data}
                        businessId={businessId}
                        businessName={businessName}
                        mode={mode}
                        onPick={() => setSheetView({ id: openDocId, mode: "pick", entry: "foot" })}
                        onPickCancel={() => (entry === "row" ? closeSheet() : setSheetView({ id: openDocId, mode: "doc", entry: "foot" }))}
                        onPickSaved={() => setSheetView({ id: openDocId, mode: "doc", entry: "foot" })}
                        onView={(v) => setSheetView({ id: openDocId, mode: v, entry: "foot" })}
                        flow={flow}
                    />
                )}
            </Drawer>
            {/* A report: the PDF in the viewer, the Baseline Snapshot drawn in-app. */}
            <ReportDrawer key={openReport?.id} report={openReport} onClose={() => setOpenReport(null)} />
        </div>
    );
}

/** Whether the filter and search leave nothing on screen (every source read). */
function sectionsEmpty(
    show: Show,
    made: ClientReportLink[] | null,
    contract: ClientReportLink[] | null,
    coded: CodedList | null,
    q: string,
    matchReport: (r: ClientReportLink) => boolean,
    matchDoc: (d: StaffDocument) => boolean,
): boolean {
    if (!q) return false;
    const r = (show === "all" || show === "reports") && (made ?? []).some(matchReport);
    const c = (show === "all" || show === "contract") && (contract ?? []).some(matchReport);
    const d = (show === "all" || show === "coded") && !!coded && coded.docs.some(matchDoc);
    return !r && !c && !d;
}

/** A section of the list: a 17px heading with its count, a line saying what is in it, then its rows. */
function Section({ title, sub, count, aside, children }: { title: string; sub: string; count: number | null; aside?: ReactNode; children: ReactNode }) {
    return (
        <section aria-label={title} className="py-6 first:pt-1 last:pb-1">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <h3 className={cn(TYPE.tt, "m-0 flex items-baseline gap-2 text-gray-900")}>
                        {title}
                        {count !== null && <span className="font-normal tabular-nums text-gray-400">{count}</span>}
                    </h3>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{sub}</p>
                </div>
                {aside}
            </div>
            <div className="mt-3">{children}</div>
        </section>
    );
}

function None() {
    return <p className={cn(TYPE.tc, "m-0 py-2 text-gray-400")}>{COPY.none}</p>;
}

function Failed({ text, onRetry }: { text: string; onRetry: () => void }) {
    return (
        <div className="flex items-center justify-between gap-3 py-3">
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{text}</p>
            <PillButton size="sm" onClick={onRetry}>{COPY.retry}</PillButton>
        </div>
    );
}
